import {
    AudioOutlined,
    DownloadOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileZipOutlined,
    FolderOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';
import { Button, Empty, Flex, Grid, List, message, Pagination, Space, Table, Tag, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CategoryType } from '../../../../../lib/global-type';
import { axiosInstance } from '../services/axios-instance';
import { DOCUMENT_ACCESS_REVOKED_MESSAGE, isDocumentAccessRevokedError } from '../services/document-access-error';
import { downloadFile } from '../services/download-file';
import { AppDispatch, RootState } from '../store';
import { setterSharingDocument } from '../store/sharing-document-slice';
import { CustomersResponseType, DocumentsResponseType, SharingDocumentType } from '../types/types';
import { TreeNodeWithMetadataType } from './directory-tree-wrapper';
import DocumentMarkupModal from './document-markup-modal';

const { Text } = Typography;

export const CATEGORY_TYPE = 'category';

const isCategory = (item: any, key = 'type'): item is CategoryType => {
    return item?.[key] === CATEGORY_TYPE;
};

export const DocumentTable = () => {
    const dispatch = useDispatch<AppDispatch>();
    const listDocument = useSelector((state: RootState) => state.sharingDocument.listDocument);
    const documentTree = useSelector((state: RootState) => state.sharingDocument.documentTree);
    const allTreeKeys = useSelector((state: RootState) => state.sharingDocument.allTreeKeys);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const [selectedTreeKey] = useSelector((state: RootState) => state.sharingDocument.selectedTreeKey);
    const selectedTreeKeyValue = selectedTreeKey != null ? String(selectedTreeKey) : '';
    const isTopLevelSelection = selectedTreeKeyValue.startsWith('customer-');

    const findNodeTree = (key: string, tree: TreeNodeWithMetadataType[]): TreeNodeWithMetadataType | null => {
        for (const node of tree) {
            if (node.key === key) {
                return node;
            }

            if (node.children && node.children.length > 0) {
                const found = findNodeTree(key, node.children as TreeNodeWithMetadataType[]);
                if (found) {
                    return found;
                }
            }
        }

        return null;
    };

    const nodeTreeChildren = findNodeTree(selectedTreeKey, documentTree)?.children || [];

    const actualListDocument: SharingDocumentType[] = [
        ...nodeTreeChildren.map((node) => ({
            id: node.key as string,
            category_id: node.key as string,
            name: node.title as string,
            path: '',
            type: 'category' as const,
            extension: 'folder',
            mime_type: 'folder',
            size: 0,
            version: 1,
            created_at: '',
            updated_at: '',
            url: '',
            customer: '',
        })),
        ...listDocument.filter((document) => {
            const categoryId = document.category_id != null ? document.category_id.toString() : '';
            const isInTree = categoryId !== '' && allTreeKeys.includes(categoryId);

            if (isInTree) {
                return categoryId === selectedTreeKeyValue;
            }

            return isTopLevelSelection;
        }),
    ];

    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        const fetchDocumentsForCustomers = async () => {
            setLoading(true);
            try {
                const customersResponse = await axiosInstance.get<CustomersResponseType>('/customers');
                const uniqueCustomers = Array.from(
                    new Set(customersResponse.data.customers.map((customer) => customer.customer).filter(Boolean)),
                );

                if (!uniqueCustomers.length) {
                    dispatch(setterSharingDocument({ listDocument: [] }));
                    return;
                }

                const listDocumentChunks = await Promise.all(
                    uniqueCustomers.map(async (customer) => {
                        try {
                            const response = await axiosInstance.get<DocumentsResponseType>('/documents', {
                                params: { customer },
                            });

                            return response.data.documents.map((document) => ({
                                ...document,
                                customer,
                                type: 'document' as const,
                            }));
                        } catch (error) {
                            console.error(`Failed to fetch documents for ${customer}:`, error);
                            return [];
                        }
                    }),
                );

                dispatch(setterSharingDocument({ listDocument: listDocumentChunks.flat() }));
            } catch (error) {
                console.error('Failed to fetch customers:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDocumentsForCustomers();
    }, []);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getFileIcon = (extension: string) => {
        const ext = extension.toLowerCase();

        if (ext === 'folder') return <FolderOutlined style={{ color: '#faad14' }} />;

        // Images
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
            return <FileImageOutlined style={{ color: '#52c41a' }} />;
        }

        // PDF
        if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#f5222d' }} />;

        // Word documents
        if (['doc', 'docx'].includes(ext)) {
            return <FileWordOutlined style={{ color: '#1890ff' }} />;
        }

        // Excel documents
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
            return <FileExcelOutlined style={{ color: '#52c41a' }} />;
        }

        // Text files
        if (['txt', 'md', 'log'].includes(ext)) {
            return <FileTextOutlined style={{ color: '#8c8c8c' }} />;
        }

        // Archives
        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
            return <FileZipOutlined style={{ color: '#722ed1' }} />;
        }

        // Video
        if (['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', 'webm'].includes(ext)) {
            return <VideoCameraOutlined style={{ color: '#eb2f96' }} />;
        }

        // Audio
        if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma'].includes(ext)) {
            return <AudioOutlined style={{ color: '#13c2c2' }} />;
        }

        // Default
        return <FileOutlined />;
    };

    const columns: ColumnType<SharingDocumentType>[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            // width: '50%',
            ellipsis: true,
            render: (name: string, record: SharingDocumentType) => {
                return (
                    <Space>
                        {getFileIcon(record.extension)}
                        <Text>{name}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Type',
            dataIndex: 'extension',
            key: 'extension',
            width: 100,
            render: (extension: string) => <Tag color="blue">{extension.toUpperCase()}</Tag>,
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            width: 120,
            render: (size: number) => (size ? formatFileSize(size) : ''),
        },
        {
            title: 'Updated',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (date: string) => (date ? formatDate(date) : ''),
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_: any, record: SharingDocumentType) => (
                <Space>
                    {record.type !== CATEGORY_TYPE && (
                        <DownloadOutlined
                            style={{ cursor: 'pointer', fontSize: 16 }}
                            onClick={async () => {
                                await handleDownload(record);
                            }}
                        />
                    )}
                </Space>
            ),
        },
    ];

    const handleDownload = async (record: SharingDocumentType) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get<SharingDocumentType>(`/document`, {
                params: {
                    customer: record.customer,
                    id: record.id,
                },
            });

            const url = response.data.url;
            if (!url) return;

            const filename = record.name || 'file';
            await downloadFile(url, filename);
        } catch (error) {
            handleDocumentAccessError(error, record, 'download');
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentOpen = async (record: SharingDocumentType) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get<SharingDocumentType>(`/document`, {
                params: {
                    customer: record.customer,
                    id: record.id,
                },
            });

            dispatch(setterSharingDocument({ openDocument: response.data }));
        } catch (error) {
            handleDocumentAccessError(error, record, 'open');
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentAccessError = (error: unknown, record: SharingDocumentType, action: 'open' | 'download') => {
        if (isDocumentAccessRevokedError(error)) {
            dispatch(
                setterSharingDocument({
                    listDocument: listDocument.filter(
                        (document) => document.id !== record.id || document.customer !== record.customer,
                    ),
                }),
            );
            message.error({
                content: DOCUMENT_ACCESS_REVOKED_MESSAGE,
                key: 'document-access-revoked',
                duration: 6,
            });
            return;
        }

        console.error(`Failed to ${action} document:`, error);
        message.error(`Unable to ${action} document. Please try again.`);
    };

    const handleCategoryOpen = (record: SharingDocumentType) => {
        dispatch(setterSharingDocument({ selectedTreeKey: [record.id] }));
    };

    const onRow = (record: SharingDocumentType) => {
        return {
            onDoubleClick: () => {
                if (isCategory(record)) {
                    handleCategoryOpen(record);
                } else {
                    handleDocumentOpen(record);
                }
            },
        };
    };

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(actualListDocument.length / pageSize));
        if (currentPage > maxPage) {
            setCurrentPage(1);
        }
    }, [actualListDocument.length, pageSize, currentPage]);

    const handlePaginationChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const pagedDocuments = actualListDocument.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <>
            {isMobile ? (
                <Flex vertical style={{ width: '100%', height: '100%' }}>
                    {actualListDocument.length ? (
                        <>
                            <div style={{ flex: 1, overflow: 'auto' }}>
                                <List
                                    loading={loading}
                                    dataSource={pagedDocuments}
                                    renderItem={(record) => (
                                        <List.Item
                                            key={record.id}
                                            style={{ padding: '12px 8px', cursor: 'pointer' }}
                                            onClick={() => {
                                                if (isCategory(record)) {
                                                    handleCategoryOpen(record);
                                                } else {
                                                    handleDocumentOpen(record);
                                                }
                                            }}
                                        >
                                            <Flex vertical style={{ width: '100%', gap: 8 }}>
                                                <Flex
                                                    justify="space-between"
                                                    align="center"
                                                    style={{ gap: 12, minWidth: 0 }}
                                                >
                                                    <Flex align="center" style={{ gap: 8, minWidth: 0, flex: 1 }}>
                                                        {getFileIcon(record.extension)}
                                                        <Text
                                                            ellipsis={{ tooltip: record.name }}
                                                            style={{ minWidth: 0, flex: 1, display: 'block' }}
                                                        >
                                                            {record.name}
                                                        </Text>
                                                    </Flex>
                                                    <Tag color="blue">{record.extension.toUpperCase()}</Tag>
                                                </Flex>
                                                <Flex justify="space-between" align="center" style={{ fontSize: 12 }}>
                                                    <Space size={12}>
                                                        <Text type="secondary">
                                                            {record.size ? formatFileSize(record.size) : ''}
                                                        </Text>
                                                        <Text type="secondary">
                                                            {record.updated_at ? formatDate(record.updated_at) : ''}
                                                        </Text>
                                                    </Space>
                                                    {!isCategory(record) && (
                                                        <Button
                                                            type="text"
                                                            icon={<DownloadOutlined />}
                                                            aria-label="Download"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleDownload(record);
                                                            }}
                                                        />
                                                    )}
                                                </Flex>
                                            </Flex>
                                        </List.Item>
                                    )}
                                />
                            </div>
                            <Flex justify="flex-end" style={{ padding: '8px 0 0', flexShrink: 0 }}>
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={actualListDocument.length}
                                    showSizeChanger={isMobile ? actualListDocument.length > pageSize : true}
                                    showTotal={(total) => `Total ${total} documents`}
                                    onChange={handlePaginationChange}
                                    onShowSizeChange={handlePaginationChange}
                                />
                            </Flex>
                        </>
                    ) : (
                        <Empty />
                    )}
                </Flex>
            ) : (
                <Table<SharingDocumentType>
                    columns={columns}
                    dataSource={actualListDocument}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: actualListDocument.length,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} documents`,
                        onChange: handlePaginationChange,
                    }}
                    scroll={{ y: 'calc(100vh - 224px)' }}
                    tableLayout="fixed"
                    onRow={onRow}
                    locale={{
                        emptyText: <Empty />,
                    }}
                />
            )}
            <DocumentMarkupModal />
        </>
    );
};
